import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faMobileAlt,
  faMapMarkerAlt,
  faSeedling,
  faTint,
  faFlask,
  faCheckCircle,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import ModalAlert from "./reusable/ModalAlert";
import getCrops from "./IssueDetails/getAllCrops";
import stateDistrictTalukaData from "../assets/static/IndiaStateDistrictTaluka";
import getNextRegNo from "./Farmer/getNextRegNo";

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  dark: "#1a3328",
  mid: "#2d5a3d",
  bright: "#3d8c5e",
  light: "#e8f5ee",
  page: "#f4f6f5",
  border: "#d8e4dc",
  white: "#ffffff",
  textDk: "#1a2e22",
  textGr: "#6c757d",
  error: "#c62828",
};

const selectTheme = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    borderColor: state.isFocused ? C.bright : C.border,
    boxShadow: state.isFocused ? `0 0 0 2px rgba(61,140,94,0.18)` : "none",
    "&:hover": { borderColor: C.bright },
    backgroundColor: C.white,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? C.mid
      : state.isFocused
        ? C.light
        : "#fff",
    color: state.isSelected ? "#fff" : C.textDk,
  }),
  placeholder: (base) => ({ ...base, color: "#aab8b0", fontSize: "0.9rem" }),
  singleValue: (base) => ({ ...base, color: C.textDk }),
};

// ─── Shared input style ───────────────────────────────────────────────────────
const inputStyle = {
  width: "100%",
  height: 40,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  padding: "0 0.85rem",
  fontSize: "0.9rem",
  color: C.textDk,
  backgroundColor: C.white,
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

// ─── Field wrapper ────────────────────────────────────────────────────────────
const FField = ({
  label,
  name,
  type = "text",
  required,
  as,
  children,
  style,
}) => (
  <div style={{ marginBottom: "1rem" }}>
    <label
      style={{
        display: "block",
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.07em",
        color: C.textGr,
        textTransform: "uppercase",
        marginBottom: "0.35rem",
      }}
    >
      {label}
      {required && <span style={{ color: C.error, marginLeft: 2 }}>*</span>}
    </label>
    {children || (
      <Field
        name={name}
        type={type}
        as={as}
        style={{ ...inputStyle, ...style }}
      />
    )}
  </div>
);

// ─── Section card ─────────────────────────────────────────────────────────────
const Section = ({ title, icon, children }) => (
  <div
    style={{
      backgroundColor: C.white,
      borderRadius: 12,
      border: `1px solid ${C.border}`,
      marginBottom: "1.25rem",
      boxShadow: "0 2px 8px rgba(20,50,30,0.04)",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        padding: "0.7rem 1.1rem",
        backgroundColor: "#f1f7f3",
        borderBottom: `1px solid ${C.border}`,
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: "0.8rem",
        fontWeight: 700,
        color: C.mid,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      }}
    >
      {icon &&
        (typeof icon === "string" ? (
          <span style={{ fontSize: "0.9rem" }}>{icon}</span>
        ) : (
          <FontAwesomeIcon icon={icon} style={{ fontSize: "0.75rem" }} />
        ))}
      {title}
    </div>
    <div style={{ padding: "1.1rem 1.25rem" }}>{children}</div>
  </div>
);

// ─── Validation schema ────────────────────────────────────────────────────────
const schema = Yup.object({
  fullName: Yup.string()
    .matches(/^[a-zA-Z ]*$/, "Letters only")
    .required("Required"),
  mobileNumber: Yup.string()
    .matches(/^\d{10}$/, "Must be 10 digits")
    .required("Required"),
  whatsappNumber: Yup.string().matches(/^\d{10}$/, "Must be 10 digits"),
  townVillage: Yup.string().matches(/^[a-zA-Z0-9 ]*$/, "Invalid characters"),
  landAcreage: Yup.string().matches(/^[a-zA-Z0-9,. ]*$/, "Invalid characters"),
  pinCode: Yup.string().matches(/^\d{6}$/, "Must be 6 digits"),
});

// ─── Main component ───────────────────────────────────────────────────────────
export const AddFarmer = () => {
  const history = useHistory();
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [allCrops, setAllCrops] = useState([]);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [regNoLoading, setRegNoLoading] = useState(false);

  useEffect(() => {
    getCrops().then((res) => {
      if (res?.status === 200) setAllCrops(res.data.map((item) => item.crop));
    });
  }, []);

  const handleSubmit = (values, { setSubmitting }) => {
    if (!selectedState?.value || !selectedDistrict?.value) {
      setErrorMsg("Please select State and District.");
      setShowError(true);
      setSubmitting(false);
      return;
    }
    const payload = {
      ...values,
      state: selectedState.value,
      district: selectedDistrict.value,
    };
    axios
      .post(`${process.env.REACT_APP_API_URL}/api/farmer/addFarmer`, {
        values: payload,
      })
      .then((res) => {
        if (res.data.message === "Farmer added successfully") {
          history.push({
            pathname: `/viewFarmer/${res.data.farmer.mobileNumber}`,
            state: { farmerID: res.data.farmer._id },
          });
        } else if (res.data.Message === "Mobile number already exists") {
          setErrorMsg("A farmer with this mobile number already exists.");
          setShowError(true);
        } else if (res.data.Message === "Farmer id already exists") {
          setErrorMsg("A farmer with this registration number already exists.");
          setShowError(true);
        } else {
          setErrorMsg("Something went wrong. Please try again.");
          setShowError(true);
        }
      })
      .catch(() => {
        setErrorMsg("Network error. Please try again.");
        setShowError(true);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div
      style={{
        backgroundColor: C.page,
        minHeight: "calc(100vh - 64px)",
        padding: "1.5rem",
      }}
    >
      {/* Page header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          marginBottom: "1.5rem",
        }}
      >
        <button
          onClick={() => history.goBack()}
          style={{
            background: "none",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: "0.35rem 0.75rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: "0.82rem",
            color: C.textGr,
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
        <div>
          <h5 style={{ margin: 0, fontWeight: 800, color: C.textDk }}>
            Add New Farmer
          </h5>
          <div style={{ fontSize: "0.75rem", color: C.textGr, marginTop: 2 }}>
            Fill in the details below to register a new farmer
          </div>
        </div>
      </div>

      <Formik
        initialValues={{
          regNo: "",
          fullName: "",
          mobileNumber: "",
          whatsappNumber: "",
          townVillage: "",
          taluka: "",
          landAcreage: "",
          previousCropsCultivated: "",
          currentCropsCultivated: "",
          irrigSource: "",
          irrigFacilities: "",
          landType: "",
          soilTestingStatus: "Not Done",
          lastSoilTestOn: "",
          manshyaProductsFeedback: "",
          followUp: "",
          existingCustomer: "No",
          usingProductsSince: "",
          paid: "No",
          phonecallio: "",
          pinCode: "",
        }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form>
            <div style={{ maxWidth: 900, margin: "0 auto" }}>
              {/* ── Personal Information ─────────────────────────────────── */}
              <Section title="Personal Information" icon={faUser}>
                <Row>
                  <Col md={3}>
                    <FField label="Farmer ID" name="regNo">
                      <Field
                        name="regNo"
                        style={{ ...inputStyle, backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                        disabled
                        placeholder={regNoLoading ? "Generating..." : "Select state to generate ID"}
                      />
                    </FField>
                  </Col>
                  <Col md={9}>
                    <FField label="Full Name" name="fullName" required>
                      <Field
                        name="fullName"
                        style={inputStyle}
                        placeholder="Enter farmer's full name"
                      />
                      {touched.fullName && errors.fullName && (
                        <div style={errStyle}>{errors.fullName}</div>
                      )}
                    </FField>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FField label="Mobile Number" name="mobileNumber" required>
                      <Field
                        name="mobileNumber"
                        style={inputStyle}
                        placeholder="10-digit mobile number"
                        maxLength={10}
                      />
                      {touched.mobileNumber && errors.mobileNumber && (
                        <div style={errStyle}>{errors.mobileNumber}</div>
                      )}
                    </FField>
                  </Col>
                  <Col md={6}>
                    <FField label="WhatsApp Number" name="whatsappNumber">
                      <Field
                        name="whatsappNumber"
                        style={inputStyle}
                        placeholder="10-digit WhatsApp number"
                        maxLength={10}
                      />
                      {touched.whatsappNumber && errors.whatsappNumber && (
                        <div style={errStyle}>{errors.whatsappNumber}</div>
                      )}
                    </FField>
                  </Col>
                </Row>
              </Section>

              {/* ── Location ─────────────────────────────────────────────── */}
              <Section title="Location" icon="📍">
                <Row>
                  <Col md={4}>
                    <FField label="State" required>
                      <Select
                        placeholder="Select State"
                        value={selectedState}
                        onChange={async (opt) => {
                          setSelectedState(opt);
                          setSelectedDistrict(null);
                          if (opt?.value) {
                            setRegNoLoading(true);
                            const res = await getNextRegNo(opt.value);
                            if (res?.data?.nextRegNo) {
                              setFieldValue("regNo", res.data.nextRegNo);
                            }
                            setRegNoLoading(false);
                          }
                        }}
                        options={Object.keys(stateDistrictTalukaData).map(
                          (s) => ({ value: s, label: s }),
                        )}
                        styles={selectTheme}
                      />
                    </FField>
                  </Col>
                  <Col md={4}>
                    <FField label="District" required>
                      <Select
                        placeholder={
                          selectedState
                            ? "Select District"
                            : "Select State first"
                        }
                        value={selectedDistrict}
                        onChange={(opt) => setSelectedDistrict(opt)}
                        isDisabled={!selectedState}
                        options={
                          selectedState
                            ? Object.keys(
                                stateDistrictTalukaData[selectedState.value] ||
                                  {},
                              ).map((d) => ({ value: d, label: d }))
                            : []
                        }
                        styles={selectTheme}
                      />
                    </FField>
                  </Col>
                  <Col md={4}>
                    <FField label="Taluka" name="taluka">
                      <Field
                        name="taluka"
                        style={inputStyle}
                        placeholder="Taluka name"
                      />
                    </FField>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <FField label="Town / Village" name="townVillage">
                      <Field
                        name="townVillage"
                        style={inputStyle}
                        placeholder="Village or town name"
                      />
                    </FField>
                  </Col>
                  <Col md={4}>
                    <FField label="Land Acreage" name="landAcreage">
                      <Field
                        name="landAcreage"
                        style={inputStyle}
                        placeholder="e.g. 5.5"
                      />
                    </FField>
                  </Col>
                  <Col md={4}>
                    <FField label="Pin Code" name="pinCode">
                      <Field
                        name="pinCode"
                        style={inputStyle}
                        placeholder="6-digit PIN code"
                        maxLength={6}
                      />
                      {touched.pinCode && errors.pinCode && (
                        <div style={errStyle}>{errors.pinCode}</div>
                      )}
                    </FField>
                  </Col>
                </Row>
              </Section>

              {/* ── Crops ────────────────────────────────────────────────── */}
              <Section title="Crop Information" icon={faSeedling}>
                <Row>
                  <Col md={6}>
                    <FField label="Currently Cultivated Crops">
                      <Select
                        placeholder="Select crops…"
                        isMulti
                        options={allCrops}
                        getOptionLabel={(o) => o}
                        getOptionValue={(o) => o}
                        onChange={(opts) =>
                          setFieldValue(
                            "currentCropsCultivated",
                            (opts || []).map((o) => o).join(","),
                          )
                        }
                        styles={selectTheme}
                      />
                    </FField>
                  </Col>
                  <Col md={6}>
                    <FField label="Previously Cultivated Crops">
                      <Select
                        placeholder="Select crops…"
                        isMulti
                        options={allCrops}
                        getOptionLabel={(o) => o}
                        getOptionValue={(o) => o}
                        onChange={(opts) =>
                          setFieldValue(
                            "previousCropsCultivated",
                            (opts || []).map((o) => o).join(","),
                          )
                        }
                        styles={selectTheme}
                      />
                    </FField>
                  </Col>
                </Row>
              </Section>

              {/* ── Farm & Irrigation ────────────────────────────────────── */}
              <Section title="Farm & Irrigation" icon={faTint}>
                <Row>
                  <Col md={4}>
                    <FField label="Irrigation Source" name="irrigSource">
                      <Field
                        name="irrigSource"
                        style={inputStyle}
                        placeholder="e.g. Borewell"
                      />
                    </FField>
                  </Col>
                  <Col md={4}>
                    <FField
                      label="Irrigation Facilities"
                      name="irrigFacilities"
                    >
                      <Field
                        name="irrigFacilities"
                        style={inputStyle}
                        placeholder="e.g. Drip"
                      />
                    </FField>
                  </Col>
                  <Col md={4}>
                    <FField label="Land Type" name="landType">
                      <Field
                        name="landType"
                        style={inputStyle}
                        placeholder="e.g. Black soil"
                      />
                    </FField>
                  </Col>
                </Row>
              </Section>

              {/* ── Soil & Customer ──────────────────────────────────────── */}
              <Section title="Soil Testing & Customer Status" icon={faFlask}>
                <Row>
                  <Col md={4}>
                    <FField label="Soil Testing Status">
                      <Field
                        as="select"
                        name="soilTestingStatus"
                        style={{ ...inputStyle, cursor: "pointer" }}
                      >
                        <option value="Not Done">Not Done</option>
                        <option value="Done">Done</option>
                        <option value="In Progress">In Progress</option>
                      </Field>
                    </FField>
                  </Col>
                  {values.soilTestingStatus === "Done" && (
                    <Col md={4}>
                      <FField label="Last Soil Tested On" name="lastSoilTestOn">
                        <Field
                          name="lastSoilTestOn"
                          type="date"
                          style={inputStyle}
                        />
                      </FField>
                    </Col>
                  )}
                  <Col md={4}>
                    <FField label="Existing Customer">
                      <Field
                        as="select"
                        name="existingCustomer"
                        style={{ ...inputStyle, cursor: "pointer" }}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </Field>
                    </FField>
                  </Col>
                  {values.existingCustomer === "Yes" && (
                    <Col md={4}>
                      <FField
                        label="Using Products Since (Year)"
                        name="usingProductsSince"
                      >
                        <Field
                          name="usingProductsSince"
                          style={inputStyle}
                          placeholder="e.g. 2020"
                        />
                      </FField>
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col md={4}>
                    <FField label="Paid Member">
                      <Field
                        as="select"
                        name="paid"
                        style={{ ...inputStyle, cursor: "pointer" }}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </Field>
                    </FField>
                  </Col>
                  <Col md={4}>
                    <FField label="Call Type">
                      <Field
                        as="select"
                        name="phonecallio"
                        style={{ ...inputStyle, cursor: "pointer" }}
                      >
                        <option value="">Select type</option>
                        <option value="Incoming">Incoming</option>
                        <option value="Outgoing">Outgoing</option>
                      </Field>
                    </FField>
                  </Col>
                </Row>
              </Section>

              {/* ── Feedback & Follow-up ─────────────────────────────────── */}
              <Section title="Feedback & Follow-up" icon={faCheckCircle}>
                <FField
                  label="Manshya Products Feedback"
                  name="manshyaProductsFeedback"
                >
                  <Field
                    as="textarea"
                    name="manshyaProductsFeedback"
                    style={{
                      ...inputStyle,
                      height: 90,
                      paddingTop: "0.55rem",
                      resize: "vertical",
                    }}
                    placeholder="Enter feedback about Manshya products…"
                  />
                </FField>
                <FField label="Follow-up Notes" name="followUp">
                  <Field
                    name="followUp"
                    style={inputStyle}
                    placeholder="Any follow-up action needed?"
                  />
                </FField>
              </Section>

              {/* ── Submit ───────────────────────────────────────────────── */}
              <div style={{ textAlign: "right", marginBottom: "2rem" }}>
                <button
                  type="button"
                  onClick={() => history.goBack()}
                  style={{
                    marginRight: "0.75rem",
                    height: 42,
                    padding: "0 1.25rem",
                    borderRadius: 9,
                    border: `1.5px solid ${C.border}`,
                    backgroundColor: C.white,
                    color: C.textDk,
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    height: 42,
                    padding: "0 2rem",
                    borderRadius: 9,
                    border: "none",
                    background: `linear-gradient(135deg, ${C.mid} 0%, #1e4530 100%)`,
                    color: "#fff",
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    opacity: isSubmitting ? 0.75 : 1,
                  }}
                >
                  {isSubmitting ? "Saving…" : "Save Farmer"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {showError && (
        <ModalAlert
          size="lg"
          variant="danger"
          message={errorMsg}
          resetCallback={setShowError}
        />
      )}
    </div>
  );
};

const errStyle = { color: C.error, fontSize: "0.76rem", marginTop: 3 };

export default AddFarmer;
